import { useRoute } from 'vue-router'
import useCdcProps from '@/composables/useCdcProps'
import useOverrides from '@/composables/useOverrides'

// Storage key prefix for all popups
const POPUP_STORAGE_PREFIX = 'mbo_popup_seen_'

// Available popup keys
export enum PopupKey {
  COMPLIANCE_FEATURES_TOUR = 'compliance_features_tour',
}

export default function usePopUp() {
  const { xprops } = useCdcProps()
  const route = useRoute()
  const { overridesSupported } = useOverrides()

  function handlePopUpLauncher() {
    if (showComplianceFeaturesPopUp()) {
      handleOpenPopUp(PopupKey.COMPLIANCE_FEATURES_TOUR)
    }
  }

  function showComplianceFeaturesPopUp(): boolean {
    // Check if overrides feature is available
    if (!overridesSupported) {
      return false
    }

    // Check if user has already seen the popup
    if (hasUserSeenPopup(PopupKey.COMPLIANCE_FEATURES_TOUR)) {
      return false
    }

    // Check if current page is home, catalog, or product page
    if (!isValidPage()) {
      return false
    }

    // Clean up old localStorage keys from previous bug
    // TODO: remove this in future releases when the bug fix has been deployed long enough
    cleanupLegacyStorageKeys()

    return true
  }

  /**
   * Remove legacy localStorage keys created by previous bug
   * This cleans up only MBO-related keys, not the entire localStorage
   */
  function cleanupLegacyStorageKeys(): void {
    try {
      const keysToRemove: string[] = []

      // Find all keys that start with problematic prefixes
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && (key.startsWith('ps_mbo-product-') || key.startsWith('ps_accounts-module-'))) {
          keysToRemove.push(key) 
        }
      }

      // Remove the identified keys
      keysToRemove.forEach(key => localStorage.removeItem(key))
    } catch (error) {
      console.warn('Failed to clean up legacy storage keys:', error)
    }
  }

  /**
   * Check if user has already seen a specific popup
   * @param popupKey - The unique key identifying the popup
   * @returns true if the popup has been seen, false otherwise
   */
  function hasUserSeenPopup(popupKey: PopupKey): boolean {
    try {
      const storageKey = `${POPUP_STORAGE_PREFIX}${popupKey}`
      const seen = localStorage.getItem(storageKey)
      return seen === 'true'
    } catch (error) {
      // If localStorage is not available, don't show popup to avoid repeated displays
      console.warn('localStorage not available:', error)
      return true
    }
  }

  /**
   * Mark a specific popup as seen
   * @param popupKey - The unique key identifying the popup
   */
  function markPopupAsSeen(popupKey: PopupKey): void {
    try {
      const storageKey = `${POPUP_STORAGE_PREFIX}${popupKey}`
      localStorage.setItem(storageKey, 'true')
    } catch (error) {
      console.warn('Failed to save popup state:', error)
    }
  }

  function isValidPage(): boolean {
    const validRoutes = ['home', 'modules', 'modules-scope', 'module']
    return validRoutes.includes(route.name as string)
  }

  /**
   * Open a popup dialog and mark it as seen
   * @param popupKey - The unique key identifying the popup
   */
  function handleOpenPopUp(popupKey: PopupKey) {
    xprops.handleOpenDialog(popupKey, { size: 'sm'})
    markPopupAsSeen(popupKey)
  }

  return {
    handlePopUpLauncher,
    hasUserSeenPopup,
    markPopupAsSeen,
  }
}
